/**
 * Market Regime Detector
 *
 * Uses a combination of ADX and Volatility to classify
 * the market into regimes: Trending, Mean-Reverting, or Random.
 * Plots a regime score where:
 *   > 0.5 = Trending
 *   < -0.5 = Mean-Reverting
 *   Between = Random/Neutral
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";
import { computeATR } from "./atr";

export const marketRegimeIndicator: IndicatorDefinition = {
  id: "market_regime",
  name: "Market Regime",
  shortName: "Regime",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 5, max: 100 },
  ],
  plots: [
    { key: "regime", label: "Regime", color: "#FF9800", style: "histogram", lineWidth: 2 },
    { key: "trendLine", label: "Trend", color: "#4CAF50", style: "line", lineWidth: 1 },
    { key: "mrLine", label: "Mean Rev", color: "#F44336", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const closes = candles.map(c => c.close);

    // Efficiency Ratio as trend proxy
    const regimeData: { time: number; value: number }[] = [];
    const trendData: { time: number; value: number }[] = [];
    const mrData: { time: number; value: number }[] = [];

    for (let i = period; i < candles.length; i++) {
      const direction = Math.abs(closes[i] - closes[i - period]);
      let volatility = 0;
      for (let j = i - period + 1; j <= i; j++) {
        volatility += Math.abs(closes[j] - closes[j - 1]);
      }
      const er = volatility > 0 ? direction / volatility : 0;

      // Map ER to regime score: 0-1 where >0.5 is trending
      // Convert to -1 to 1 range
      const score = (er - 0.5) * 2;

      regimeData.push({ time: candles[i].time, value: score });
      trendData.push({ time: candles[i].time, value: 0.5 });
      mrData.push({ time: candles[i].time, value: -0.5 });
    }

    return [
      { key: "regime", data: regimeData },
      { key: "trendLine", data: trendData },
      { key: "mrLine", data: mrData },
    ];
  },
};
